/**
 * useScreenWakeLock — keeps the device screen awake while a lesson is
 * open. User mandate 2026-02-21: "Ensure screen doesn't fall asleep
 * during lessons."
 *
 * Uses the Screen Wake Lock API. Silently no-ops on browsers/devices
 * that don't support it (older Safari, some in-app webviews) — the
 * caller doesn't need to feature-detect. Releases on unmount and on
 * `visibilitychange` (browsers auto-drop the lock when the tab hides,
 * so we re-acquire when it comes back).
 */
import { useEffect, useRef } from "react";

export default function useScreenWakeLock(enabled = true) {
  const lockRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !navigator.wakeLock) return;

    let cancelled = false;

    const request = async () => {
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await lock.release(); } catch { /* ignore */ }
          return;
        }
        lockRef.current = lock;
        // Auto-clear our ref if the OS releases it (e.g. tab hidden).
        lock.addEventListener("release", () => {
          if (lockRef.current === lock) lockRef.current = null;
        });
      } catch {
        // NotAllowedError, NotSupportedError, or SecurityError — all
        // benign. Nothing else in the app depends on the lock.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !lockRef.current) {
        request();
      }
    };

    request();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (lockRef.current) {
        try { lockRef.current.release(); } catch { /* ignore */ }
        lockRef.current = null;
      }
    };
  }, [enabled]);
}
